// Shared plane dropdown — one Radix `DropdownMenu` styled as a `HudChip`,
// consumed by BOTH the board HUD (`PlaneSwitcher`, null entry "All" clears the
// filter) and the view builder's `on` clause row (null entry "— primary —" —
// the implicit whole-model plane; picking it removes the `on` narrowing).
// Omit `clearLabel` for a no-clear picker (null then shows the placeholder).
// Presentational: the host owns the plane list + selection.
//
// Built on Radix `DropdownMenu` (the house overlay standard) — open/close,
// dismiss, focus, keyboard nav, positioning and portalling are the library's.

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Layers, ChevronDown, Check } from "lucide-react";
import { Tooltip } from "@archlang/ui";
import { HudChip, type HudChipAccent } from "../HudChip";

export interface PlaneSelectProps {
  readonly planes: readonly string[];
  /** Selected plane, or null = the clear entry / placeholder. */
  readonly value: string | null;
  readonly onChange: (plane: string | null) => void;
  /** Label for the null entry (HUD: "All", clears the filter). Omit → no null
   *  entry; a null `value` then renders `placeholder` on the trigger. */
  readonly clearLabel?: string;
  /** Trigger text when `value` is null and there's no `clearLabel`. */
  readonly placeholder?: string;
  /** Chip accent while a plane is selected (default `sky`). */
  readonly accent?: HudChipAccent;
  readonly "aria-label"?: string;
  readonly title?: string;
  /** Passed through to the trigger chip (e.g. the HUD's `data-hud-region`). */
  readonly "data-hud-region"?: string;
}

// Radix RadioGroup values are strings; the null selection needs a sentinel.
const CLEAR = "__clear__";

export function PlaneSelect({
  planes,
  value,
  onChange,
  clearLabel,
  placeholder = "plane…",
  accent = "sky",
  "aria-label": ariaLabel = "Plane",
  title = "Focus on one plane's presence subset",
  "data-hud-region": region,
}: PlaneSelectProps) {
  if (planes.length === 0) return null;
  const options = clearLabel !== undefined ? [CLEAR, ...planes] : planes;
  const triggerText = value ?? clearLabel ?? placeholder;
  return (
    <DropdownMenu.Root>
      <Tooltip content={title}>
        <DropdownMenu.Trigger asChild>
          <HudChip
            data-hud-region={region}
            aria-label={ariaLabel}
            active={value !== null}
            accent={accent}
          >
            <Layers width={13} height={13} aria-hidden="true" className="shrink-0" />
            <span className={value === null && clearLabel === undefined ? "opacity-60" : undefined}>
              {triggerText}
            </span>
            <ChevronDown width={12} height={12} aria-hidden="true" className="shrink-0 opacity-70" />
          </HudChip>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-overlay min-w-40 overflow-hidden rounded-md border border-slate-700 bg-slate-900 py-1 text-xs shadow-xl shadow-black/50"
        >
          <DropdownMenu.RadioGroup
            value={value ?? CLEAR}
            onValueChange={(v) => onChange(v === CLEAR ? null : v)}
          >
            {options.map((v) => (
              <DropdownMenu.RadioItem
                key={v}
                value={v}
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-slate-300 outline-none data-[highlighted]:bg-slate-800 data-[highlighted]:text-slate-100"
              >
                <span className="flex-1">{v === CLEAR ? clearLabel : v}</span>
                <DropdownMenu.ItemIndicator>
                  <Check className="h-3.5 w-3.5 shrink-0 text-arch-400" aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
